import {
  createSearchParams,
  type Location,
  type NavigateOptions,
  useLocation,
  useNavigate,
} from 'react-router';

import { type RouteEnum } from '@/layouts/routes/Route.model';
import { LocalStorageParam, setLocalStorage } from '@/utils/localStorage.util';

export type PathParams = { params: Record<string, string>; pathname: Path } | Path;
type Path = RouteEnum | string;

interface Return {
  goBack: (fallbackPath: PathParams, options?: NavigateOptions) => void;
  location: Location;
  push: (path: PathParams, options?: NavigateOptions) => void;
}

// View transitions are the default navigation policy for the whole app;
// callers opt out with { viewTransition: false }.
const withViewTransition = (options?: NavigateOptions): NavigateOptions => ({
  viewTransition: true,
  ...options,
});

/**
 * Thin navigation bridge (OCD-309): view transitions and last-visited
 * bookkeeping only. Authorization, initialization and privacy-policy
 * requirements are enforced by route guard loaders, so every entry path —
 * pushes, direct URLs — goes through the same mechanism.
 */
export const useOcdHistory = (): Return => {
  const navigate = useNavigate();
  const location = useLocation();

  const push: Return['push'] = (path, options) => {
    const navigationOptions = withViewTransition(options);

    setLocalStorage(LocalStorageParam.LAST_VISITED_LOCATION, path);

    if (typeof path === 'object') {
      const search = createSearchParams(path.params).toString();
      void navigate(
        {
          pathname: path.pathname,
          search: search ? `?${search}` : '',
        },
        navigationOptions,
      );
      return;
    }
    void navigate(path, navigationOptions);
  };

  const goBack: Return['goBack'] = (fallbackPath, options) => {
    const historyIndex = (window.history.state as { idx?: unknown } | null)?.idx;
    if (typeof historyIndex === 'number' && historyIndex > 0) {
      // react-router's delta overload `navigate(delta)` accepts no options (viewTransition
      // included), so history-back cannot carry them; options apply to the fallback push.
      void navigate(-1);
      return;
    }
    push(fallbackPath, options);
  };

  return { goBack, location, push };
};
